import React, { useEffect, useState } from 'react';
import { Lock, ShieldCheck } from 'lucide-react';
import api from '../../lib/api';

const RISK_COLORS = {
  Low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Medium: 'bg-amber-50 text-amber-700 border-amber-200',
  'Medium-High': 'bg-orange-50 text-orange-700 border-orange-200',
};

export default function InvestmentsTab({ onUpgrade }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/investments').then(({ data }) => setData(data)).catch(console.error);
  }, []);

  if (!data) return <div className="text-[#5C5C66] text-sm p-4">Loading recommendations...</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white border border-[#E5E5EB] rounded-2xl p-6">
        <div className="flex items-start gap-3">
          <ShieldCheck size={20} className="text-[#534AB7] mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-[#0A0A0B] mb-1">Malaysian Investment Recommendations</h3>
            <p className="text-sm text-[#5C5C66]">
              {data.personalized
                ? 'Ranked for your income, savings, debt and risk preference. Not financial advice — always do your own research.'
                : 'Create a plan first to get these personalised to your numbers.'}
            </p>
          </div>
        </div>
      </div>

      {data.recommendations.map((rec) => (
        rec.locked ? (
          <div key={rec.id} data-testid={`investment-locked-${rec.id}`} className="bg-white border border-dashed border-[#D5D5DE] rounded-2xl p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#F6F6F9] flex items-center justify-center flex-shrink-0">
                  <Lock size={18} className="text-[#A1A1AA]" />
                </div>
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-[#5C5C66]">{rec.name}</h4>
                  <p className="text-sm text-[#A1A1AA] mt-0.5">
                    Risk: {rec.risk_level} · From {rec.min_investment}
                  </p>
                </div>
              </div>
              <button
                data-testid={`unlock-investment-btn-${rec.id}`}
                onClick={onUpgrade}
                className="bg-[#534AB7] text-white hover:bg-[#423B99] rounded-xl px-5 py-2.5 font-semibold text-sm whitespace-nowrap"
              >
                Unlock with Premium
              </button>
            </div>
          </div>
        ) : (
          <div key={rec.id} data-testid={`investment-${rec.id}`} className="bg-white border border-[#E5E5EB] rounded-2xl p-6 sm:p-8">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <h4 className="text-lg sm:text-xl font-semibold text-[#0A0A0B] mr-auto">{rec.name}</h4>
              <span className={`text-xs font-semibold border rounded-full px-3 py-1 ${RISK_COLORS[rec.risk_level] || 'bg-[#F6F6F9] text-[#5C5C66] border-[#E5E5EB]'}`}>
                {rec.risk_level} risk
              </span>
              <span className="text-xs font-semibold bg-[#534AB7]/10 text-[#534AB7] rounded-full px-3 py-1">
                Min. {rec.min_investment}
              </span>
            </div>
            <div className="space-y-3 text-sm sm:text-base leading-relaxed">
              <p className="text-[#5C5C66]"><span className="font-semibold text-[#0A0A0B]">What it is: </span>{rec.what_it_is}</p>
              <p className="text-[#5C5C66]"><span className="font-semibold text-[#0A0A0B]">Why it suits you: </span>{rec.why_it_suits_you}</p>
              <p className="text-[#5C5C66] bg-[#F6F6F9] rounded-xl p-4"><span className="font-semibold text-[#0A0A0B]">In plain language: </span>{rec.plain_explanation}</p>
            </div>
          </div>
        )
      ))}
    </div>
  );
}
